import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Page, PageHeader } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Loader2, FileSearch, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import RigheReview from "@/components/import-concorrente/RigheReview";
import Confronto from "@/components/import-concorrente/Confronto";

function UploadBox({ onFile, busy, progresso }) {
  const ref = useRef(null);
  const caricando = busy && progresso < 100;
  return (
    <div className="bg-white border-2 border-dashed border-zinc-300 rounded-lg p-10 text-center max-w-2xl" data-testid="imp-upload-box"
      onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]); }}>
      {busy ? (
        <div className="flex flex-col items-center gap-3 text-zinc-600" data-testid="imp-analisi-in-corso">
          <Loader2 className="h-10 w-10 animate-spin" />
          <div className="font-medium">{caricando ? `Caricamento file… ${progresso}%` : "L'AI sta leggendo il preventivo e abbinando le voci…"}</div>
          {caricando
            ? <div className="w-64 h-2 bg-zinc-100 rounded overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${progresso}%` }} data-testid="imp-progresso" /></div>
            : <div className="text-xs text-zinc-500">Di solito ci vuole meno di un minuto.</div>}
        </div>
      ) : (
        <>
          <FileSearch className="h-10 w-10 mx-auto text-zinc-400 mb-3" />
          <div className="font-semibold mb-1">Carica il preventivo dell'altra azienda</div>
          <div className="text-sm text-zinc-500 mb-4">PDF oppure foto (anche scattata col telefono), fino a 100 MB. Il file serve solo per la lettura e non viene conservato.</div>
          <input ref={ref} type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) onFile(f); }} data-testid="imp-file-input" />
          <Button onClick={() => ref.current?.click()} data-testid="imp-upload-btn" style={{ background: "var(--brand)", color: "white" }}><Upload className="h-4 w-4 mr-2" />Scegli file o scatta foto</Button>
        </>
      )}
    </div>
  );
}

export default function ImportaPreventivo() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [righe, setRighe] = useState([]);
  const [mq, setMq] = useState(0);
  const [totConc, setTotConc] = useState(0);
  const [calc, setCalc] = useState(null);
  const [voci, setVoci] = useState([]);
  const [errore, setErrore] = useState("");
  const [progresso, setProgresso] = useState(0);
  const vociMap = Object.fromEntries(voci.map((v) => [v.id, v]));

  useEffect(() => {
    Promise.all([api.get("/voci-backoffice"), api.get("/composite-sections").catch(() => ({ data: [] }))]).then(([v, s]) => {
      const priceOf = Object.fromEntries((s.data || []).flatMap((sec) => (sec.voci || []).map((x) => [x.id, x.price])));
      setVoci((v.data || []).map((x) => (priceOf[x.id] != null ? { ...x, prezzo_rivendita: priceOf[x.id] } : x)));
    });
  }, []);

  const onFile = async (file) => {
    setBusy(true); setErrore(""); setProgresso(0);
    const fail = (msg) => { setErrore(msg); toast.error(msg); };
    try {
      if (file.size > 100 * 1024 * 1024) { fail("File troppo grande (max 100 MB)"); return; }
      // Upload a pezzi da 4 MB: nessun limite del proxy sui file grandi
      const CH = 4 * 1024 * 1024;
      const total = Math.max(1, Math.ceil(file.size / CH));
      const uploadId = Array.from(crypto.getRandomValues(new Uint8Array(12))).map((b) => b.toString(16).padStart(2, "0")).join("");
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId); fd.append("index", String(i)); fd.append("total", String(total));
        fd.append("chunk", file.slice(i * CH, (i + 1) * CH), "chunk");
        await api.post("/import-concorrente/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setProgresso(Math.round(((i + 1) / total) * 100));
      }
      const { data } = await api.post("/import-concorrente/analizza-upload", { upload_id: uploadId, filename: file.name, content_type: file.type });
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: job } = await api.get(`/import-concorrente/jobs/${data.job_id}`);
        if (job.stato === "completato") {
          const r = job.result;
          setRes(r); setRighe(r.righe || []); setMq(r.mq_stimati || 0);
          setTotConc(r.totale_documento || (r.righe || []).reduce((s, x) => s + (x.totale || 0), 0));
          toast.success(`${(r.righe || []).length} righe lette`);
          return;
        }
        if (job.stato === "errore") { fail(job.errore || "Lettura non riuscita"); return; }
      }
      fail("L'analisi sta impiegando troppo: riprova");
    } catch (e) {
      const st = e.response?.status;
      fail(e.response?.data?.detail || (st === 413 ? "File troppo grande" : st ? `Errore caricamento (HTTP ${st})` : "Connessione non riuscita: riprova"));
    }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (!res) return;
    const t = setTimeout(() => {
      api.post("/import-concorrente/calcola", { mq, righe }).then((r) => setCalc(r.data)).catch(() => toast.error("Errore nel calcolo del confronto"));
    }, 400);
    return () => clearTimeout(t);
  }, [res, righe, mq]);

  const nota = `Preventivo ricostruito da preventivo ${res?.azienda ? `di ${res.azienda}` : "concorrente"} (totale concorrente € ${Number(totConc || 0).toFixed(2)} IVA esclusa).`;
  const missingPrice = () => {
    const n = righe.filter((r) => r.includi && !r.voce_id && (r.prezzo_nostro == null || r.prezzo_nostro === "")).length;
    if (n) toast.error(`Ci sono ${n} righe senza voce e senza nostro prezzo: completale o togli la spunta`);
    return n > 0;
  };

  const createComposite = async () => {
    if (missingPrice()) return;
    const { data: secs } = await api.get("/composite-sections").catch(() => ({ data: [] }));
    const inComposite = new Set((secs || []).flatMap((s) => (s.voci || []).map((v) => v.id)));
    const priceOf = Object.fromEntries((secs || []).flatMap((s) => (s.voci || []).map((v) => [v.id, v.price])));
    const selections = {}; const manual_extras = [];
    righe.filter((r) => r.includi).forEach((r) => {
      const v = vociMap[r.voce_id]; const q = Number(r.qty_nostra) || 0;
      if (v && inComposite.has(v.id)) selections[v.id] = { qty: (selections[v.id]?.qty || 0) + q, price: priceOf[v.id] ?? v.prezzo_rivendita };
      else if (v) manual_extras.push({ name: v.name, category: v.category || "EXTRA", unit: v.unit, qty: q, price: v.prezzo_rivendita, prezzo_acquisto: v.prezzo_acquisto, ricarico: v.ricarico });
      else manual_extras.push({ name: r.descrizione, category: "EXTRA", unit: r.unit || "pz", qty: q, price: Number(r.prezzo_nostro) || 0, prezzo_acquisto: null, ricarico: null });
    });
    sessionStorage.setItem("composite_prefill", JSON.stringify({ mq, selections, manual_extras, note: nota }));
    nav("/preventivocomposite?prefill=1");
  };

  const createPacchetto = (p) => {
    if (!mq) return toast.error("Inserisci i metri quadri per usare un pacchetto");
    if (missingPrice()) return;
    const extras = p.extras.map((e, i) => ({ voce_id: e.voce_id || `imp-${e.idx ?? i}`, name: e.name, unit: e.unit, qty: e.qty, unit_price: e.unit_price || 0 }));
    sessionStorage.setItem("preventivo_prefill", JSON.stringify({ package_id: p.package_id, mq, extras, note: nota }));
    nav("/preventivopacchetto?prefill=1");
  };

  return (
    <div>
      <PageHeader title="Preventivo da concorrente" subtitle="L'AI legge il preventivo di un'altra azienda e lo ricostruisce con le nostre voci"
        actions={res && <Button variant="outline" onClick={() => { setRes(null); setRighe([]); setCalc(null); }} data-testid="imp-restart"><RotateCcw className="h-4 w-4 mr-1" />Carica un altro</Button>} />
      <Page>
        {!res ? (<>
          {errore && !busy && <div className="mb-4 bg-rose-50 border border-rose-200 text-rose-800 rounded-lg p-3 text-sm" data-testid="imp-errore"><b>Lettura non riuscita:</b> {errore}. Riprova con un altro file (PDF o foto).</div>}
          <UploadBox onFile={onFile} busy={busy} progresso={progresso} />
        </>) : (
          <div className="space-y-6">
            <div className="flex flex-wrap gap-4 items-end bg-white border border-zinc-200 rounded-lg p-4">
              <div><div className="text-xs text-zinc-500">Azienda</div><div className="font-semibold" data-testid="imp-azienda">{res.azienda || "—"}</div></div>
              <div><Label className="text-xs">Metri quadri immobile</Label><Input type="number" min={0} value={mq} onChange={(e) => setMq(Number(e.target.value) || 0)} className="h-9 w-28" data-testid="imp-mq" /></div>
              <div><Label className="text-xs">Totale concorrente € (IVA escl.)</Label><Input type="number" min={0} value={totConc} onChange={(e) => setTotConc(Number(e.target.value) || 0)} className="h-9 w-36" data-testid="imp-totale-input" /></div>
            </div>
            <Confronto calc={calc} totaleConcorrente={totConc} onCreateComposite={createComposite} onCreatePacchetto={createPacchetto} />
            <RigheReview righe={righe} setRighe={setRighe} voci={voci} vociMap={vociMap} />
          </div>
        )}
      </Page>
    </div>
  );
}
